
import Box from "../../../shared/Box"
import Modal from "../../../shared/Modal"
import ConfirmModal from "../../../shared/ConfirmModal"
import RowActionsMenu from "../../../shared/RowActionsMenu"
import Button from "../../../shared/Button"
import { showError } from "../../../shared/toast"
import { FinanceTableData } from "../modal/FinanceContext"
import { useFinance } from "../modal/FinanceContext"
import { useNavigate } from "react-router-dom"
import { History, Pencil, Trash2, X } from "lucide-react"
import { useState } from "react"
import { fetchEditHistory } from "../api/financeApi"

interface EditHistoryEntry {
    id: number;
    field_name: string;
    old_value: string;
    new_value: string;
    edited_by: string;
    edited_at: string;
}

const hexToRowBg = (hex: string) => {
    if (!hex || hex.length < 7 || hex[0] !== '#') return undefined;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return undefined;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    const alpha = brightness < 80 ? 0.9 : 0.75;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const FinanceTable = () => {
    const { financeList, financeSummary, setIsDeleteModal, isDeleteModal, handleFinanceDelete } = useFinance()
    const tableDataClassName = "py-4 px-4 text-sm text-foreground font-inter w-[10%] truncate"
    const tableHeadingClassName = "py-3 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter w-[10%]"
    const navigate = useNavigate()

    const [historyModal, setHistoryModal] = useState<{ financeId: string; entries: EditHistoryEntry[] } | null>(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [deletingRecord, setDeletingRecord] = useState(false);

    const handleUpdate = (finance: FinanceTableData) => {
        navigate(`/admin/finance/update-finance/${finance.FinanceId}`);
    }

    const confirmDeleteFinance = async () => {
        if (!isDeleteModal) return;
        setDeletingRecord(true);
        try {
            await handleFinanceDelete(isDeleteModal);
        } catch {
            showError("Could not delete record. Please try again.");
        } finally {
            setDeletingRecord(false);
        }
    };

    const handleShowHistory = async (finance: FinanceTableData) => {
        setHistoryLoading(true);
        try {
            const data = await fetchEditHistory(parseInt(finance.FinanceId || "0"));
            setHistoryModal({ financeId: finance.FinanceId || "", entries: data });
        } catch (error) {
            console.error("Failed to load edit history:", error);
        }
        setHistoryLoading(false);
    };

    return (
        <>
            <Box boxMainDivClasses="mt-[30px]" boxClass="bg-surface border-border">
                <div className="w-full overflowXAuto">
                    <table className="w-full">
                        <thead className="bg-surface-muted">
                            <tr className="border-b border-border">
                                <th className={`${tableHeadingClassName}`}>
                                    Date
                                </th>
                                <th className={`${tableHeadingClassName} w-[20%]`}>
                                    Description
                                </th>
                                <th className={`${tableHeadingClassName}`}>
                                    Amount
                                </th>
                                <th className={`${tableHeadingClassName} hidden md:table-cell`}>
                                    Tax
                                </th>
                                <th className={`${tableHeadingClassName} hidden lg:table-cell`}>
                                    Cheque #
                                </th>
                                <th className={`${tableHeadingClassName} hidden sm:table-cell`}>
                                    Category
                                </th>
                                <th className={`${tableHeadingClassName} hidden lg:table-cell`}>
                                    Added By
                                </th>
                                <th className={`${tableHeadingClassName} hidden xl:table-cell`}>
                                    Created At
                                </th>
                                <th className={`${tableHeadingClassName}`}>
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {financeList.map((data: FinanceTableData, index: number) => {
                                const bgColor = data.CategoryColor ? hexToRowBg(data.CategoryColor) : undefined;
                                const rowBg = bgColor ? { backgroundColor: bgColor } : undefined;
                                return (
                                <tr key={index} className="border-t border-solid border-border">
                                    <td className={`${tableDataClassName}`} style={rowBg}>
                                        {data.Date}
                                    </td>
                                    <td className={`${tableDataClassName} w-[20%]`} style={rowBg}>
                                        <div className="w-full truncate max-w-[200px]" title={data.Description}>
                                            {data.Description}
                                        </div>
                                    </td>
                                    <td className={`${tableDataClassName}`} style={rowBg}>
                                        {(() => {
                                            const isIncome = data.CategoryName?.startsWith("Income");
                                            const formatted = data.Amount?.toLocaleString();
                                            return isIncome ? formatted : `-${formatted}`;
                                        })()}
                                    </td>
                                    <td className={`${tableDataClassName} hidden md:table-cell`} style={rowBg}>
                                        {data.TaxDeductions?.toLocaleString()}
                                    </td>
                                    <td className={`${tableDataClassName} hidden lg:table-cell`} style={rowBg}>
                                        {data.ChequeNumber || "—"}
                                    </td>
                                    <td className={`${tableDataClassName} hidden sm:table-cell`} style={rowBg}>
                                        {data.CategoryName}
                                    </td>
                                    <td className={`${tableDataClassName} hidden lg:table-cell`} style={rowBg}>
                                        {data.AddedBy}
                                    </td>
                                    <td className={`${tableDataClassName} hidden xl:table-cell`} style={rowBg}>
                                        {data.CreatedAt}
                                    </td>
                                    <td className={`${tableDataClassName}`} style={rowBg}>
                                        <RowActionsMenu
                                            actions={[
                                                ...(data.HasEdits ? [{
                                                    label: "View edit history",
                                                    icon: History,
                                                    onClick: () => handleShowHistory(data),
                                                }] : []),
                                                { label: "Edit", icon: Pencil, onClick: () => handleUpdate(data) },
                                                { label: "Delete", icon: Trash2, onClick: () => setIsDeleteModal(data), destructive: true },
                                            ]}
                                        />
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                        {financeList.length > 0 && (
                            <tfoot>
                                <tr className="border-t-2 border-border">
                                    <td className="py-4 px-4 text-sm font-semibold text-foreground font-inter" colSpan={2}>Totals (filtered)</td>
                                    <td className="py-4 px-4 text-sm font-semibold font-inter text-foreground" colSpan={7}>
                                        <div className="flex items-center gap-6">
                                            <span className="text-emerald-400">Income: {financeSummary.total_income.toLocaleString()}</span>
                                            <span className="text-red-400">Expense: -{financeSummary.total_expense.toLocaleString()}</span>
                                            <span className={financeSummary.net >= 0 ? "text-emerald-400" : "text-red-400"}>
                                                Net: {financeSummary.net >= 0 ? "" : "-"}{Math.abs(financeSummary.net).toLocaleString()}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </Box>

            {/* Delete Confirmation Modal */}
            {isDeleteModal && (
                <ConfirmModal
                    title="Delete Finance Record"
                    message="Are you sure you want to delete this record? This action cannot be undone."
                    confirmLabel="Delete"
                    variant="danger"
                    confirming={deletingRecord}
                    onConfirm={confirmDeleteFinance}
                    onCancel={() => setIsDeleteModal(null)}
                />
            )}

            {/* Edit History Modal */}
            {historyModal && (
                <Modal size="lg" hideCloseButton onClick={() => setHistoryModal(null)} closeButtonCLick={() => setHistoryModal(null)} modalClassName="max-h-[80vh]">
                    <div className="flex items-center justify-between p-5 border-b border-border">
                        <h3 className="text-lg font-semibold text-foreground font-inter">
                            Edit History — Record #{historyModal.financeId}
                        </h3>
                        <Button disableDefaultStyles onClick={() => setHistoryModal(null)} buttonClasses="p-1.5 hover:bg-surface-muted rounded-lg transition-colors text-muted-foreground hover:text-foreground">
                            <X className="w-5 h-5" />
                        </Button>
                    </div>
                    <div className="overflow-auto max-h-[60vh] p-5">
                            {historyLoading ? (
                                <p className="text-muted-foreground text-sm font-inter text-center py-8">Loading...</p>
                            ) : historyModal.entries.length === 0 ? (
                                <p className="text-muted-foreground text-sm font-inter text-center py-8">No edit history found.</p>
                            ) : (
                                <div className="space-y-4">
                                    {(() => {
                                        // Group entries by date (day)
                                        const groups: { date: string; entries: EditHistoryEntry[] }[] = [];
                                        historyModal.entries.forEach((entry) => {
                                            const dateKey = new Date(entry.edited_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
                                            const last = groups[groups.length - 1];
                                            if (last && last.date === dateKey) {
                                                last.entries.push(entry);
                                            } else {
                                                groups.push({ date: dateKey, entries: [entry] });
                                            }
                                        });
                                        return groups.map((group, gi) => (
                                            <div key={gi} className="bg-surface-muted border border-border rounded-2xl overflow-hidden">
                                                <div className="px-4 py-2.5 bg-surface-muted border-b border-border">
                                                    <span className="text-xs font-medium text-muted-foreground font-inter">
                                                        {group.date}
                                                    </span>
                                                </div>
                                                <table className="w-full">
                                                    <thead>
                                                        <tr className="border-b border-border">
                                                            <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter">Time</th>
                                                            <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter">Field</th>
                                                            <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter">Old Value</th>
                                                            <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter">New Value</th>
                                                            <th className="py-2 px-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider font-inter">Edited By</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {group.entries.map((entry) => (
                                                            <tr key={entry.id} className="border-t border-border">
                                                                <td className="py-2.5 px-4 text-sm text-muted-foreground font-inter whitespace-nowrap">{new Date(entry.edited_at).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase()}</td>
                                                                <td className="py-2.5 px-4 text-sm text-foreground font-inter capitalize">{entry.field_name.replace(/_/g, ' ')}</td>
                                                                <td className="py-2.5 px-4 text-sm text-red-400 font-inter">{entry.old_value}</td>
                                                                <td className="py-2.5 px-4 text-sm text-green-400 font-inter">{entry.new_value}</td>
                                                                <td className="py-2.5 px-4 text-sm text-muted-foreground font-inter">{entry.edited_by}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            )}
                        </div>
                </Modal>
            )}
        </>
    )
}

export default FinanceTable
